import BASE_CHECK_TYPE from '../../../constants/regular.constant';

/**
 * 法国 SIGN FR 开店四步提交前校验用的税务字段（已 trim）。
 * 不用于普通保存表单。
 */
export type FranceFiskalyRegisterFields = {
    /** 公司名 / UNIT 名 */
    reg_name?: string;
    /** 税务街道 */
    reg_local?: string;
    /** 邮编 */
    reg_postcode?: string;
    /** SIREN 或 FR VAT */
    reg_vat_number?: string;
    /** 城市 */
    reg_city?: string;
    /** 税务国家，须为 FR */
    reg_country?: string;
    /** 门牌号 */
    reg_street_number?: string;
    /** 省/州（选填） */
    reg_state?: string;
    /** 联系邮箱（步骤 3 选填） */
    reg_pec?: string;
};

/** 校验失败时返回的字段与 i18n 信息 */
export type FranceFiskalyValidationFailure = {
    /** 固定失败标记 */
    ok: false;
    /** 失败字段名，便于拼字段文案 */
    fieldKey: string;
    /** 错误文案 i18n key */
    messageKey: string;
    /** i18n 插值（如长度上下限） */
    messageParams?: Record<string, number | string>;
};

/** 校验成功 */
export type FranceFiskalyValidationSuccess = {
    /** 固定成功标记 */
    ok: true;
};

/** 法国 Fiskaly 四步格式校验结果 */
export type FranceFiskalyValidationResult =
    | FranceFiskalyValidationSuccess
    | FranceFiskalyValidationFailure;

/**
 * 用 BASE_CHECK_TYPE 条目校验单个字段；失败时带上 helperText / i18nRule。
 * @param fieldKey 字段名
 * @param value 已 trim 的值
 * @param ruleKey BASE_CHECK_TYPE 键名
 */
const failIfRuleMismatch = (
    fieldKey: string,
    value: string,
    ruleKey: keyof typeof BASE_CHECK_TYPE,
): FranceFiskalyValidationFailure | null => {
    const rule = BASE_CHECK_TYPE[ruleKey];
    if (!rule || rule.regular.test(value)) {
        return null;
    }
    return {
        ok: false,
        fieldKey,
        messageKey: rule.helperText,
        // BASE_CHECK_TYPE.i18nRule 声明为 object；此处收窄为 i18n 插值对象
        messageParams: rule.i18nRule as Record<string, string | number> | undefined,
    };
};

/**
 * 校验法国 SIGN FR 开店四步所需税务字段格式。
 * 调用方须先做必填非空；本函数专注正则（含选填省、选填邮箱）。
 * @param fields 已 trim 的字段
 * @param options.validatePec 为 true 时，若填写了 reg_pec 则校验邮箱格式
 */
export function validateFranceFiskalyRegisterFields(
    fields: FranceFiskalyRegisterFields,
    options?: { validatePec?: boolean },
): FranceFiskalyValidationResult {
    /** 必填格式字段：字段名 → 规则 key */
    const requiredChecks: Array<{ key: keyof FranceFiskalyRegisterFields; rule: keyof typeof BASE_CHECK_TYPE }> = [
        { key: 'reg_country', rule: 'franceCountryFR' },
        { key: 'reg_name', rule: 'franceFiskalyAddress120' },
        { key: 'reg_local', rule: 'franceFiskalyAddress120' },
        { key: 'reg_city', rule: 'franceFiskalyAddress120' },
        { key: 'reg_postcode', rule: 'franceFiskalyPostcode' },
        { key: 'reg_street_number', rule: 'franceFiskalyAddress20' },
        { key: 'reg_vat_number', rule: 'franceVatNumber' },
    ];

    for (const item of requiredChecks) {
        const raw = fields[item.key];
        const value = typeof raw === 'string' ? raw.trim() : '';
        // 同邮箱：未填不校格式；有值才跑正则（开店必填由调用方单独做非空检查）
        if (!value) {
            continue;
        }
        const failure = failIfRuleMismatch(item.key, value, item.rule);
        if (failure) {
            return failure;
        }
    }

    const stateRaw = typeof fields.reg_state === 'string' ? fields.reg_state : '';
    if (stateRaw) {
        const stateFailure = failIfRuleMismatch('reg_state', stateRaw, 'franceFiskalyStateOptional');
        if (stateFailure) {
            return stateFailure;
        }
    }

    if (options?.validatePec) {
        const pec = typeof fields.reg_pec === 'string' ? fields.reg_pec.trim() : '';
        if (pec) {
            const pecFailure = failIfRuleMismatch('reg_pec', pec, 'email');
            if (pecFailure) {
                return pecFailure;
            }
        }
    }

    return { ok: true };
}
